import { filter, includes, intersection, isEmpty } from 'lodash';
import React, { useMemo } from 'react';

import { FormikSelect, FormikSelectProps } from 'components';
import { useUserContext } from 'hooks';
import { P2PProviderTradeMethods } from 'types';
import { formUtils } from 'utils';

type Props = {
  name: string;
  label: string;
  assetCurrencyIds?: string[];
  exclude?: string[];
  tradeMethodsIds?: string[];
  tradeMethodsByProviderId?: Record<string, P2PProviderTradeMethods>;
  ignoreTradeMethodsIds?: boolean;
  hideArchived?: boolean;
} & Partial<FormikSelectProps>;

export const P2PProviderSelect: React.FC<Props> = ({
  name,
  label,
  assetCurrencyIds,
  exclude = [],
  tradeMethodsIds = [],
  tradeMethodsByProviderId = {},
  ignoreTradeMethodsIds = false,
  hideArchived = false,
  ...rest
}) => {
  const { p2pProviders } = useUserContext();

  const availableP2PProviders = useMemo(
    () =>
      filter(p2pProviders, (p2pProvider) => {
        if (!ignoreTradeMethodsIds) {
          const providerTradeMethods = tradeMethodsByProviderId[p2pProvider.id];
          const p2pProviderTradeMethodsId = [
            ...(providerTradeMethods?.payinConfig?.tradeMethodIds || []),
            ...(providerTradeMethods?.payoutConfig?.tradeMethodIds || []),
          ];

          if (
            !intersection(tradeMethodsIds, p2pProviderTradeMethodsId).length
          ) {
            return false;
          }
        }

        if (
          !isEmpty(assetCurrencyIds) &&
          !includes(assetCurrencyIds, p2pProvider.assetCurrencyId)
        ) {
          return false;
        }

        if (exclude?.includes(p2pProvider.id)) {
          return false;
        }

        if (hideArchived && p2pProvider.archived) {
          return false;
        }

        return true;
      }),
    [
      p2pProviders,
      tradeMethodsByProviderId,
      tradeMethodsIds,
      assetCurrencyIds,
      exclude,
      ignoreTradeMethodsIds,
      hideArchived,
    ],
  );

  const providersOptions = useMemo(
    () => formUtils.getOptions(availableP2PProviders),
    [availableP2PProviders],
  );

  return (
    <FormikSelect
      noneOption
      label={label}
      {...rest}
      name={name}
      options={providersOptions}
    />
  );
};
